/**
 * Feeds API - Activities
 *
 * Get feed activities.
 */

import { z } from 'zod'
import { PaginationParamsSchema } from '../common'
import { CoverImageSchema, FeedAlbumSchema } from '../../schemas/entities/feed'
import { FeedSyncedEmbedSchema } from '../../schemas/entities/synced-embed'
import { FeedActivitySchema, StreamReactionUserSchema as ReactionUserSchema } from './common'

// ============================================================================
// GET /v1/feeds/:feedId/activities
// ============================================================================

export const GetFeedActivitiesRequestSchema = PaginationParamsSchema.extend({
  feedId: z.string(),
  feed_group: z.enum(['group', 'timeline']).optional(),
  userId: z.string().optional(),
  idGt: z.string().optional(),
  idGte: z.string().optional(),
  idLt: z.string().optional(),
  idLte: z.string().optional()
})

export type GetFeedActivitiesRequest = z.infer<typeof GetFeedActivitiesRequestSchema>

export const GetFeedActivitiesResponseSchema = z.object({
  data: z.array(FeedActivitySchema),
  message: z.string(),
  next: z.string().nullable().optional()
})

export type GetFeedActivitiesResponse = z.infer<typeof GetFeedActivitiesResponseSchema>

// ============================================================================
// GET /v1/feeds/activities/:activityId
// ============================================================================

export const ActivityDetailRequestSchema = z.object({
  activityId: z.string().min(1)
})

export type ActivityDetailRequest = z.infer<typeof ActivityDetailRequestSchema>

export const UserProfileDataSchema = z.object({
  publicId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  profileImageUrl: z.string().nullable(),
  customImageUrl: z.string().nullable(),
  platformId: z.string().nullable()
})

export const SchoolDataSchema = z.object({
  id: z.string(),
  name: z.string(),
  // School-rung artwork for the attribution ladder — the last fallback when a
  // post resolves to neither a person, a byline, nor a space. Light and dark are
  // separate files (see the same pair on the feed-list `school` block in
  // `schemas/entities/feed.ts`), picked by the viewer's active color scheme.
  // Deliberately NOT `logoUrl` / `logoThumbnailUrl`: those are populated on 0 and
  // 15 of 103 production schools respectively. Optional so older responses parse.
  logoUrlLight: z.string().nullable().optional(),
  logoUrlDark: z.string().nullable().optional()
})

export const SectionDataSchema = z.object({
  id: z.string(),
  name: z.string(),
  sectionCode: z.string().nullable()
})

/**
 * The space an activity is presented as coming from FOR THE READING VIEWER.
 *
 * A multi-audience broadcast fans out to N feeds but carries ONE publisher
 * primary, so naming that primary tells most recipients the wrong space (and
 * tapping it 404s — they are not in it). This is the viewer-resolved answer:
 * the primary when the viewer follows it, else the first fan-out audience the
 * viewer DOES follow. `id` is the `UserGroup.publicId` to open, matching the
 * timeline badge's `group.id`; `kind` says which id-space the activity was
 * addressed in (`section` = section-backed space, `group` = community group).
 */
export const ActivitySourceSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['section', 'group'])
})

export type ActivitySource = z.infer<typeof ActivitySourceSchema>

export const FileAttachmentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  fileUrl: z.string(),
  fileSize: z.number().nullable(),
  mimeType: z.string().nullable(),
  // Type discriminator so a reader never has to infer a document from the file
  // extension. Set server-side for every attachment: Media-Library rows map from
  // `MediaType`, and rows on the deprecated `FilesOnContentRecords` relation —
  // which carry no type column — derive one from their stored mime. Optional so
  // a client parsing an older response still validates.
  type: z.enum(['image', 'video', 'file']).optional(),
  /**
   * Length in seconds, set only on a row that states one — in practice an AUDIO
   * attachment, from the duration the client measured at pick time (SCH-2130).
   * Optional + nullable: absent on responses from a backend predating it, and
   * `null` for a recording nothing could measure. A reader must treat both as
   * "unknown length" and take the total from its own player once it loads,
   * never as a zero-length track.
   */
  duration: z.number().nullable().optional(),
  /**
   * Mux public playback id — set only on a Mux-backed VIDEO whose encode has
   * finished. Same field, same meaning as the feed-list attachment
   * (`schemas/entities/feed.ts`) and the media-library payload (`formatMedia`),
   * so the detail read and the list read cannot drift on how a client
   * recognises a Mux video. Build delivery URLs from it with the shared
   * builders in `api/media/mux-urls.ts` — never by hand.
   *
   * When it is set, `fileUrl` is the HLS manifest for the same asset. When the
   * encode is still running, `fileUrl` carries the `mux-pending://<mediaId>`
   * sentinel instead and this field is absent.
   */
  muxPlaybackId: z.string().nullable().optional()
})

export type FileAttachment = z.infer<typeof FileAttachmentSchema>

export const FeedContentRecordSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  bodyFormat: z.string(),
  contentType: z.string(),
  publishedAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Nullable since 2026-08-08: institutional content (inbound email, a
  // sync-published post, a deleted account) has no author. The previous
  // fallback fabricated `{firstName: 'Unknown', lastName: 'User'}` and shipped
  // it as a real person; `null` lets clients render the space or a school byline
  // instead. Mobile's `normalizePostToActivity` already reads
  // `contentRecord.author || {}`, so null degrades to its own display fallback.
  author: UserProfileDataSchema.nullable(),
  publisher: UserProfileDataSchema.nullable(),
  // Free-text byline from the source system (Blackbaud `author`), e.g. "IT Dept"
  // or "PA Co-Presidents". Not a resolvable person, so neither `author` nor
  // `publisher` above can carry it — both require real name fields. Mirrors the
  // feed-list field of the same name (`schemas/entities/feed.ts`) so a post reads
  // identically in the list and on the detail screen. Optional + nullable:
  // absent on older responses, `null` when the school left the box empty.
  byline: z.string().nullable().optional(),
  attachments: z.array(FileAttachmentSchema),
  // Render-time cover for the post, resolved server-side with the same precedence as the
  // feed list (CUSTOM rule → rule URL, NONE → null, AUTO/no-rule → legacy cover). Optional +
  // nullable: absent on older responses, `null` for a NONE rule / no cover. See feed parity fix
  // docs/_archive/2026-10-17-plans-sweep/plans/2026-07-10-007-fix-activity-detail-cover-parity-plan.md.
  coverImageUrl: z.string().nullable().optional(),
  coverImage: CoverImageSchema.nullable().optional(),
  // SCH-2109: the shared album on an album post, same shape as the feed list's
  // `object.album` so the detail screen renders the same label. Optional.
  album: FeedAlbumSchema.nullable().optional(),
  // Same two fields as the feed list's `object` (`schemas/entities/feed.ts`):
  // whether the post is a Blackbaud-synced news item or announcement, and its
  // sanitized `metadata.embeds`. Optional so older responses parse (SCH-1104).
  isSyncedPost: z.boolean().optional(),
  embeds: z.array(FeedSyncedEmbedSchema).optional()
})

export type FeedContentRecord = z.infer<typeof FeedContentRecordSchema>

/** Per-kind reaction summary surfaced on a single-activity read. */
export const ActivityReactionSummarySchema = z.object({
  id: z.string(),
  type: z.string(),
  count: z.number(),
  hasReacted: z.boolean()
})

export type ActivityReactionSummary = z.infer<typeof ActivityReactionSummarySchema>

export const ActivityDetailSchema = z.object({
  id: z.string(),
  activityType: z.string(),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // SCH-177 (Apple-compliance tombstone). When the resolved activity's author is
  // in the viewer's block set, the single-activity read returns a neutral
  // "content unavailable" tombstone instead of the full activity (NOT a 404 —
  // the only entry points are deep links and stale push notifications, where a
  // 404 reads as breakage and a tombstone reads as intentional). On a tombstone
  // every content-bearing field is blanked: `contentRecord` is null and
  // `section` is null. Block is one-directional and this is only ever the
  // blocker's own view, so nothing about the blocked author is leaked. Absent /
  // false on every normal activity; clients treat absent as a normal activity.
  isUnavailable: z.boolean().optional(),
  school: SchoolDataSchema.nullable(),
  section: SectionDataSchema.nullable(),
  // Viewer-aware source space (2026-08-08). `section` is the DB section — the
  // publisher's single primary, and absent entirely for community-group content.
  // `source` is the space THIS viewer received the activity through. Optional +
  // nullable so it is purely additive: absent on older responses, `null` when no
  // viewer-aware answer exists (no Stream row, no viewer, no membership hit).
  // Clients should prefer `source` and fall back to `section`.
  source: ActivitySourceSchema.nullable().optional(),
  contentRecord: FeedContentRecordSchema.nullable(),
  // Engagement state for the requesting user, enriched from Stream on read (the
  // DB mirror does not store reactions). Present — with real numbers, zeros
  // included — whenever Stream answered, INCLUDING for a viewer with no
  // membership at the school, who is read as the platform system user and so
  // gets the post's real counts with `hasReacted: false`. Absent only alongside
  // `engagementUnavailable`. `ownReactions` mirrors the feed shape (kind →
  // array) so clients can derive the filled-heart state uniformly.
  //
  // Every field here stays OPTIONAL: the shipped 1.8.23 binary reads this
  // contract and must keep parsing responses unchanged.
  reactionCount: z.number().optional(),
  commentCount: z.number().optional(),
  reactions: z.array(ActivityReactionSummarySchema).optional(),
  ownReactions: z.record(z.string(), z.array(z.object({ id: z.string() }))).optional(),
  // SCH-1974 — `true` when the server could NOT determine engagement for this
  // read (Stream timed out, errored, or is not configured). The four fields
  // above are then omitted and the client must KEEP the count it already holds
  // rather than treating the absence as zero. Absent on every successful read,
  // including one where the post genuinely has no Stream activity — that case
  // reports real zeros, so a client does correct a stale value. Additive: an
  // older client simply ignores the field.
  engagementUnavailable: z.boolean().optional(),
  // Whether the underlying content allows comments / reactions. Sourced from the
  // ContentRecord (DB default true). Clients gate the comments + hearts UI on
  // these; absent (non-content activity) is treated as enabled by the client.
  commentsEnabled: z.boolean().optional(),
  reactionsEnabled: z.boolean().optional()
})

export const ActivityDetailResponseSchema = z.object({
  message: z.string(),
  data: ActivityDetailSchema
})

export type ActivityDetailResponse = z.infer<typeof ActivityDetailResponseSchema>

// ============================================================================
// GET /v1/feeds/activities/:activityId/reactions
// ============================================================================

export const ActivityReactionsListRequestSchema = z.object({
  activity_id: z.string().min(1),
  kind: z.string().optional(),
  limit: z.number().int().positive().max(100).optional(),
  offset: z.number().int().min(0).optional(),
  user_id: z.string().optional(),
  id_gt: z.string().optional(),
  id_gte: z.string().optional(),
  id_lt: z.string().optional(),
  id_lte: z.string().optional()
})

export type ActivityReactionsListRequest = z.infer<typeof ActivityReactionsListRequestSchema>

export const ReactionItemSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  user: ReactionUserSchema.nullable(),
  kind: z.string(),
  activity_id: z.string(),
  data: z
    .object({
      text: z.string().optional()
    })
    .optional(),
  parent: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  latest_children: z.record(z.string(), z.array(z.any())).optional(),
  own_children: z.record(z.string(), z.array(z.any())).optional(),
  children_counts: z.record(z.string(), z.number()).optional()
})

export type ReactionItem = z.infer<typeof ReactionItemSchema>

export const ActivityReactionsListResponseSchema = z.object({
  message: z.string(),
  data: z.array(ReactionItemSchema),
  meta: z.object({
    limit: z.number(),
    offset: z.number(),
    next: z.string().optional(),
    duration: z.string().optional(),
    total: z.number().optional()
  })
})

export type ActivityReactionsListResponse = z.infer<typeof ActivityReactionsListResponseSchema>
